import { Alert, Box, Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { Holder } from '../../data/types';
import { holderReadiness } from '../../domain/kyc/readiness';
import {
  WHOLESALE_MODE_LABELS,
  wholesaleModeFor,
  wholesaleStatus,
} from '../../domain/kyc/wholesale';
import { useRegistry } from '../../store/RegistryStore';
import { date, DEMO_TODAY } from '../../lib/format';
import { KYC_TYPE_LABELS } from '../../lib/labels';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { ConfirmDialog, useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';
import PeopleChecks from './PeopleChecks';
import RiskRatingCard from './RiskRatingCard';

export default function HolderComplianceTab({ holder }: { holder: Holder }) {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const [settingStatus, setSettingStatus] = useState(false);
  const mode = wholesaleModeFor(state);
  const wholesale = wholesaleStatus(holder, mode, DEMO_TODAY);
  const toast = useToast();
  const cases = scope.kycCases.filter((item) => item.holderId === holder.id);
  const readiness = holderReadiness(holder, scope.kycCases, DEMO_TODAY, undefined, mode);
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <Alert
        severity={readiness.ready ? (readiness.issues.length ? 'info' : 'success') : 'warning'}
        sx={{ gridColumn: { lg: '1 / -1' } }}
      >
        <strong>
          {readiness.ready ? 'Ready to invest' : 'Not ready to invest'}
          {readiness.issues.length ? ':' : '. Nothing outstanding.'}
        </strong>
        {readiness.issues.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {readiness.issues.map((issue) => (
              <li key={issue.text}>
                {issue.text}
                {issue.blocking ? '' : ' (to watch)'}
              </li>
            ))}
          </Box>
        )}
      </Alert>
      <SectionCard
        title="Identity verification (KYC)"
        action={
          <Button
            size="small"
            onClick={() => toast('Re-verification request sent to the investor app', 'info')}
          >
            Request re-verification
          </Button>
        }
      >
        <KeyValueGrid
          items={[
            { label: 'Status', value: <StatusChip status={holder.kycStatus} /> },
            { label: 'Verified', value: date(holder.kycVerifiedAt) },
            { label: 'Next refresh due', value: date(holder.kycRefreshDue) },
            { label: 'Provider', value: 'GreenID (demo) · DFAT and PEP screening daily' },
          ]}
        />
      </SectionCard>
      <SectionCard title="Tax residency, TFN and CRS/FATCA">
        <KeyValueGrid
          items={[
            {
              label: 'TFN / ABN',
              value:
                holder.tfnStatus === 'QUOTED' ? (
                  `TFN quoted (••• ••• ${holder.tfnLast3})`
                ) : holder.tfnStatus === 'ABN_QUOTED' ? (
                  `ABN quoted (${holder.abn})`
                ) : (
                  <StatusChip status={holder.tfnStatus} />
                ),
            },
            { label: 'Tax residency', value: holder.residency },
            { label: 'CRS', value: <StatusChip status={holder.crs} /> },
            { label: 'FATCA', value: <StatusChip status={holder.fatca} /> },
          ]}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          TFNs are encrypted with a separate key and only ever shown masked. Access is logged.
        </Typography>
      </SectionCard>
      <PeopleChecks holder={holder} />
      <RiskRatingCard holder={holder} />
      <SectionCard
        title="Wholesale investor status"
        sx={{ gridColumn: { lg: '1 / -1' } }}
        action={
          mode === 'MANUAL' ? (
            <Button size="small" onClick={() => setSettingStatus(true)}>
              {wholesale.qualified ? 'Change status' : 'Record as wholesale'}
            </Button>
          ) : (
            <StatusChip
              status={wholesale.qualified ? 'VERIFIED' : 'FAILED'}
              label={wholesale.qualified ? 'Wholesale' : 'Not wholesale'}
            />
          )
        }
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {wholesale.qualified
            ? `${wholesale.route}${
                wholesale.governing?.expiresAt
                  ? ` · valid until ${date(wholesale.governing.expiresAt)}`
                  : ''
              }.`
            : wholesale.manual
              ? 'Not yet set by staff.'
              : `No valid certificate for ${wholesale.missing.join(', ')}.`}
        </Typography>
        <DataTable
          dense
          rows={[
            {
              id: holder.id,
              name: holder.name,
              role: 'Investing entity',
              certificate: holder.wholesale,
            },
            ...holder.beneficialOwners
              .filter((owner) => owner.role !== 'BENEFICIARY')
              .map((owner) => ({
                id: owner.id,
                name: owner.name,
                role: owner.role.charAt(0) + owner.role.slice(1).toLowerCase(),
                certificate: owner.wholesale,
              })),
          ]}
          rowKey={(row) => row.id}
          columns={[
            {
              key: 'name',
              label: 'Certificate held by',
              render: (row) => (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.role}
                  </Typography>
                </>
              ),
            },
            {
              key: 'issuer',
              label: 'Issued by',
              render: (row) => row.certificate?.issuer ?? 'No certificate',
            },
            {
              key: 'expires',
              label: 'Expires',
              render: (row) =>
                row.certificate
                  ? row.certificate.expiresAt && row.certificate.expiresAt < DEMO_TODAY
                    ? `Expired ${date(row.certificate.expiresAt)}`
                    : date(row.certificate.expiresAt)
                  : '—',
            },
          ]}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
          Rule: {WHOLESALE_MODE_LABELS[mode]} (set in Fund defaults; each fund can choose its own).
          A certificate certifies one person or entity and covers every entity a certified person
          controls. Accountant certificates last two years.
        </Typography>
        <ConfirmDialog
          open={settingStatus}
          title={wholesale.qualified ? 'Remove wholesale status' : 'Record as wholesale'}
          body="Recorded against your name in the audit log. Keep the supporting evidence on the holder's documents."
          confirmLabel={wholesale.qualified ? 'Remove status' : 'Record as wholesale'}
          requireComment
          commentLabel="Basis, e.g. professional investor with $10m+ gross assets"
          onClose={() => setSettingStatus(false)}
          onConfirm={(reason) => {
            dispatch({
              type: 'HOLDER_WHOLESALE_OVERRIDE',
              holderId: holder.id,
              qualified: !wholesale.qualified,
              reason,
            });
            toast(wholesale.qualified ? 'Wholesale status removed' : 'Recorded as wholesale');
            setSettingStatus(false);
          }}
        />
      </SectionCard>
      <SectionCard title="KYC & AML cases" noPadding sx={{ gridColumn: { lg: '1 / -1' } }}>
        <DataTable
          rows={cases}
          rowKey={(row) => row.id}
          dense
          emptyMessage="No cases for this holder"
          columns={[
            { key: 'type', label: 'Case', render: (row) => KYC_TYPE_LABELS[row.type] },
            { key: 'opened', label: 'Opened', render: (row) => date(row.openedAt) },
            { key: 'detail', label: 'Detail', render: (row) => row.detail },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
          ]}
        />
      </SectionCard>
    </Box>
  );
}
